import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Server-side session record. The browser holds only the session ID in a
 * signed httpOnly cookie; everything authoritative lives in this Firestore
 * doc. Linda (admin) can revoke a session by deleting the doc — next request
 * fails the lookup and the browser is forced back to /login.
 *
 * Firestore path: sessions/{sessionId}
 *
 * sessionId is 256 bits of CSPRNG entropy, base64url-encoded (43 chars).
 * Cookie value is `s:<sessionId>.<hmac>` per the cookie-parser convention,
 * signed with SESSION_COOKIE_SECRET.
 */
export const SessionSchema = z.object({
    id: DocId,
    userId: DocId,
    tenantId: TenantId,
    /**
     * Microsoft Entra object ID — denormalised from the user doc for one-hop
     * audit-log writes that don't need a user lookup.
     */
    entraOid: z.string().uuid(),
    createdAt: IsoTimestamp,
    /**
     * Expiry. Sliding window: the session middleware extends this on every
     * request that lands on it (rate-limited via lastSeenAt — only extend if
     * we haven't extended recently). 30 days from createdAt by default.
     */
    expiresAt: IsoTimestamp,
    lastSeenAt: IsoTimestamp,
    /**
     * User-agent string at session creation. Used for the audit log + session
     * list ("Active sessions") in the admin panel later.
     */
    userAgent: z.string().max(500).optional(),
    /**
     * Originating IP at session creation. Hashed before storage so we can
     * detect "same source" without storing raw addresses (PII reduction).
     * SHA-256 hex.
     */
    ipHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    /**
     * Demo-omgeving: de persoonlijke toegangscode (`demo_access_codes/{id}`)
     * waarmee deze sessie is aangemaakt. authenticate.ts controleert per verzoek
     * of die code nog geldig is; ingetrokken of verlopen = sessie weg. Afwezig
     * op Entra-sessies en op sessies van het oude gedeelde demo-wachtwoord.
     */
    demoCodeId: z.string().min(1).max(100).optional(),
    /**
     * Wisselknop (besluit Jos 26 okt 2026): gezet = deze sessie werkt in RHOGO
     * in plaats van de eigen tenant. Afwezig = eigen tenant. Alleen `rhogo` is
     * toegestaan, zodat een gecorrumpeerd of gemanipuleerd doc nooit naar een
     * willekeurige tenant kan wijzen; `tenantId` hierboven blijft de thuistenant.
     * authenticate.ts controleert het recht bij elk verzoek opnieuw.
     */
    actieveTenantId: z.literal('rhogo').optional(),
});
//# sourceMappingURL=session.js.map